import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { apiService } from "../../../api/api";
import { MusicAuditStats } from "../../../utils/@types";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import { buildQueryString, parseStringParam } from "../../../utils/urlFilters";
import { trackToolAction } from "../../../utils/analytics";
import { ROUTES } from "../../../config/routes";

type StatsFilters = {
  legalEntityType: string;
  legalEntity: string;
  channelId: string;
};

const DEFAULT_FILTERS: StatsFilters = {
  legalEntityType: "",
  legalEntity: "",
  channelId: "",
};

const DEFAULT_LEGAL_ENTITY_TYPE = "MoonbugMusicLabel";

const buildStatsParams = (auditType: string, filters: StatsFilters) => ({
  auditType,
  legalEntityType: filters.legalEntityType || DEFAULT_LEGAL_ENTITY_TYPE,
  legalEntity: filters.legalEntity || "ALL",
  channelId: filters.channelId || "ALL",
  channelManagerName: "ALL",
});

const formatNumber = (value?: number) =>
  (value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const MusicLabelDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      legalEntityType:
        parseStringParam(params, "legalEntityType") || DEFAULT_LEGAL_ENTITY_TYPE,
      legalEntity: parseStringParam(params, "legalEntity") || "ALL",
      channelId: parseStringParam(params, "channelId"),
    };
  }, [location.search]);

  const [pendingFilters, setPendingFilters] = useState<StatsFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });
  const [appliedFilters, setAppliedFilters] = useState<StatsFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });

  useEffect(() => {
    setPendingFilters((prev) => ({
      ...prev,
      ...initialFilters,
    }));
    setAppliedFilters((prev) => ({
      ...prev,
      ...initialFilters,
    }));
  }, [initialFilters]);

  const [legalEntityTypeOptions, setLegalEntityTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [channelIdOptions, setChannelIdOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [incorrectStats, setIncorrectStats] = useState<MusicAuditStats | null>(
    null
  );
  const [mismatchedStats, setMismatchedStats] =
    useState<MusicAuditStats | null>(null);
  const [stubStats, setStubStats] = useState<MusicAuditStats | null>(null);
  const [missingStats, setMissingStats] = useState<MusicAuditStats | null>(null);

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    setAppliedFilters(pendingFilters);
    const query = buildQueryString({
      legalEntityType: pendingFilters.legalEntityType || DEFAULT_LEGAL_ENTITY_TYPE,
      legalEntity: pendingFilters.legalEntity || "ALL",
      channelId: pendingFilters.channelId,
    });
    navigate({ search: query }, { replace: true });
  }, [navigate, pendingFilters]);

  const handleReset = useCallback(() => {
    setPendingFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    navigate({ search: "" }, { replace: true });
  }, [navigate]);

  const buildAuditQuery = useCallback(
    (includeChannelIdAlias?: boolean) =>
      buildQueryString({
        legalEntityType:
          pendingFilters.legalEntityType || DEFAULT_LEGAL_ENTITY_TYPE,
        legalEntity: pendingFilters.legalEntity || "ALL",
        channelId: pendingFilters.channelId,
        ChannelId: includeChannelIdAlias ? pendingFilters.channelId : undefined,
      }),
    [pendingFilters]
  );

  useEffect(() => {
    apiService.musicAssetAudit
      .getMusicAuditStatsFilters({ legalEntityType: DEFAULT_LEGAL_ENTITY_TYPE })
      .then((data) => {
        setLegalEntityTypeOptions(
          (data.legalEntityTypes || []).map((value) => ({
            label: value,
            value,
          }))
        );
        setLegalEntityOptions([
          { label: "All", value: "ALL" },
          ...(data.legalEntities || []).map((value) => ({
            label: value,
            value,
          })),
        ]);
        setChannelIdOptions(
          (data.channelIds || []).map((value) => ({ label: value, value }))
        );
        setPendingFilters((prev) => ({
          ...prev,
          legalEntityType: prev.legalEntityType || DEFAULT_LEGAL_ENTITY_TYPE,
          legalEntity: prev.legalEntity || "ALL",
        }));
        setAppliedFilters((prev) => ({
          ...prev,
          legalEntityType: prev.legalEntityType || DEFAULT_LEGAL_ENTITY_TYPE,
          legalEntity: prev.legalEntity || "ALL",
        }));
      })
      .catch((error) => {
        console.error("Error fetching music audit filters:", error);
        setPendingFilters((prev) => ({
          ...prev,
          legalEntityType: prev.legalEntityType || DEFAULT_LEGAL_ENTITY_TYPE,
          legalEntity: prev.legalEntity || "ALL",
        }));
        setAppliedFilters((prev) => ({
          ...prev,
          legalEntityType: prev.legalEntityType || DEFAULT_LEGAL_ENTITY_TYPE,
          legalEntity: prev.legalEntity || "ALL",
        }));
      });
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      const [incorrect, mismatched, stub, missing] = await Promise.all([
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("incorrectAssetTypes", appliedFilters)
        ),
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("mismatchedSoundRecordings", appliedFilters)
        ),
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("stubAssets", appliedFilters)
        ),
        apiService.musicAssetAudit.getMusicAuditStats(
          buildStatsParams("missingSoundRecordings", appliedFilters)
        ),
      ]);

      setIncorrectStats(incorrect);
      setMismatchedStats(mismatched);
      setStubStats(stub);
      setMissingStats(missing);
    };

    fetchStats().catch((error) => {
      console.error("Error fetching music label stats:", error);
    });
  }, [appliedFilters]);

  const summaryCards = useMemo(
    () => [
      {
        title: "Incorrect Asset Types",
        totalLabel: "Total Incorrect Asset Types",
        stats: incorrectStats,
        uniqueValue: incorrectStats?.uniqueAssetIds,
        uniqueLabel: "Unique Asset IDs",
      },
      {
        title: "Mismatched Sound Recordings",
        totalLabel: "Total Mismatches",
        stats: mismatchedStats,
        uniqueValue: mismatchedStats?.uniqueAssetCodes,
        uniqueLabel: "Unique Asset Codes",
      },
      {
        title: "Stub Assets",
        totalLabel: "Total Issues",
        stats: stubStats,
        uniqueValue: stubStats?.uniqueCustomIds,
        uniqueLabel: "Unique Custom IDs",
      },
      {
        title: "Missing Sound Recordings",
        totalLabel: "Total Issues",
        stats: missingStats,
        uniqueValue: missingStats?.uniqueAssetCodes,
        uniqueLabel: "Unique Asset Codes",
      },
    ],
    [incorrectStats, mismatchedStats, stubStats, missingStats]
  );

  return (
    <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <div className="grid-container" style={{ marginTop: "-1rem" }}>
          <div className="filter-item">
            <text style={{ color: "grey", fontSize: "small" }}>
              Legal Entity Type
            </text>
            <MultiSelect
              value={
                pendingFilters.legalEntityType
                  ? [pendingFilters.legalEntityType]
                  : []
              }
              options={legalEntityTypeOptions}
              onChange={(e) =>
                setPendingFilters((prev) => ({
                  ...prev,
                  legalEntityType: e.value[0] || "",
                }))
              }
              maxSelectedLabels={1}
              optionLabel="label"
              style={{ width: "90%", borderRadius: "5px" }}
              filter
              showClear
            />
          </div>
          <div className="filter-item">
            <text style={{ color: "grey", fontSize: "small" }}>
              Legal Entity
            </text>
            <MultiSelect
              value={
                pendingFilters.legalEntity ? [pendingFilters.legalEntity] : []
              }
              options={legalEntityOptions}
              onChange={(e) =>
                setPendingFilters((prev) => ({
                  ...prev,
                  legalEntity: e.value[0] || "",
                }))
              }
              maxSelectedLabels={1}
              optionLabel="label"
              style={{ width: "90%", borderRadius: "5px" }}
              filter
              showClear
            />
          </div>
          <div className="filter-item">
            <text style={{ color: "grey", fontSize: "small" }}>Channel ID</text>
            <MultiSelect
              value={pendingFilters.channelId ? [pendingFilters.channelId] : []}
              options={channelIdOptions}
              onChange={(e) =>
                setPendingFilters((prev) => ({
                  ...prev,
                  channelId: e.value[0] || "",
                }))
              }
              maxSelectedLabels={1}
              optionLabel="label"
              style={{ width: "90%", borderRadius: "5px" }}
              filter
              showClear
            />
          </div>
        </div>
        <div className="grid-container">
          <div
            className="flex align-items-center justify-content-between gap-3"
            style={{ marginLeft: "0.1vh" }}
          >
            <div className="flex align-items-center gap-3">
              <div style={{ paddingTop: "1.5rem" }}>
                <Button
                  label="Search"
                  icon="pi pi-search"
                  onClick={handleSearch}
                />
              </div>
              <div style={{ paddingTop: "1.5rem" }}>
                <Button
                  label="Reset"
                  icon="pi pi-replay"
                  severity="danger"
                  onClick={handleReset}
                />
              </div>
            </div>
            <div className="flex align-items-center flex-wrap gap-2">
              <Button
                label="Music Video Embedding"
                size="small"
                onClick={() =>
                  navigate(
                    `${ROUTES.MUSIC_VIDEO_EMBEDDING}${buildAuditQuery(true)}`
                  )
                }
              />
              <Button
                label="Music Video Metadata"
                size="small"
                onClick={() =>
                  navigate(
                    `${ROUTES.MUSIC_VIDEO_METADATA}${buildAuditQuery()}`
                  )
                }
              />
              <Button
                label="Asset Type"
                size="small"
                onClick={() =>
                  navigate(`${ROUTES.ASSET_TYPE_AUDIT}${buildAuditQuery()}`)
                }
              />
              <Button
                label="Stub Asset"
                size="small"
                onClick={() =>
                  navigate(`${ROUTES.STUB_ASSET_AUDIT}${buildAuditQuery()}`)
                }
              />
              <Button
                label="SoundRecording Mismatch"
                size="small"
                onClick={() =>
                  navigate(
                    `${ROUTES.MISMATCHED_SOUND_RECORDINGS}${buildAuditQuery()}`
                  )
                }
              />
            </div>
          </div>
        </div>
      </Card>

      {summaryCards.map((card) => (
        <div key={card.title}>
          <h3 style={{ marginTop: "1.5rem" }}>{card.title}</h3>
          <Card
            className="mb-3"
            style={{
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "7px",
            }}
          >
            <div className="grid">
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-blue-600">
                    {formatNumber(card.stats?.totalIssues)}
                  </div>
                  <div className="text-sm text-gray-600">{card.totalLabel}</div>
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-green-600">
                    {formatNumber(card.uniqueValue)}
                  </div>
                  <div className="text-sm text-gray-600">{card.uniqueLabel}</div>
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-orange-600">
                    {formatNumber(card.stats?.newIssues)}
                  </div>
                  <div className="text-sm text-gray-600">
                    New Issues (7 days)
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className="text-center p-3">
                  <div className="text-2xl font-bold text-purple-600">
                    {formatNumber(card.stats?.totalApproxDailyViews)}
                  </div>
                  <div className="text-sm text-gray-600">Total Daily Views</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default MusicLabelDashboard;
